import { ethers } from 'ethers';
import { SWM_ABI } from '@/abi/SWM';
import { swmAddress, poolAddress } from '@/constants';
import { NetworkConnectivityHelper } from '@/utils/networkConnectivity';
import { poolHistoryService } from './poolHistoryService';

export interface WalletTransactionRow {
  token: string;
  amount: string;
  usdValue?: string;
  operation: 'Deposit' | 'Transfer In' | 'Transfer Out';
  quantity: number;
  source: string;
  destination: string;
  blockNumber: number;
  txHash: string;
  timestamp?: Date;
}

export class WalletTransactionsService {
  private provider: ethers.BrowserProvider | null = null;

  private async ensureProvider() {
    if (!this.provider && typeof window !== 'undefined' && window.ethereum) {
      this.provider = new ethers.BrowserProvider(window.ethereum);
    }
    if (!this.provider) throw new Error('MetaMask provider not available');
  }

  private async getSafeRange() {
    await this.ensureProvider();
    const range = await NetworkConnectivityHelper.getSafeBlockRange(
      this.provider!,
      5000
    );
    if (!range) {
      // Fallback to last 10000 blocks
      const latest = await this.provider!.getBlockNumber();
      return { fromBlock: Math.max(0, latest - 10000), toBlock: latest };
    }
    return range;
  }

  private async getSwmContract() {
    await this.ensureProvider();
    return new ethers.Contract(swmAddress, SWM_ABI, this.provider!);
  }

  private normalizeAmount(raw: bigint, decimals: number = 9): string {
    try {
      return ethers.formatUnits(raw, decimals);
    } catch {
      return raw.toString();
    }
  }

  async getSwmTransfers(userAddress: string): Promise<WalletTransactionRow[]> {
    await this.ensureProvider();
    const contract = await this.getSwmContract();
    const { fromBlock, toBlock } = await this.getSafeRange();

    const user = ethers.getAddress(userAddress);

    // Two queries: outgoing (from=user), incoming (to=user)
    const outgoingFilter = contract.filters.Transfer(user);
    const incomingFilter = contract.filters.Transfer(null, user);

    const [outLogs, inLogs] = await Promise.all([
      contract.queryFilter(outgoingFilter, fromBlock, toBlock),
      contract.queryFilter(incomingFilter, fromBlock, toBlock),
    ]);

    const rows: WalletTransactionRow[] = [];

    for (const ev of outLogs) {
      const log = ev as ethers.EventLog;
      const [from, to, value] = log.args as unknown as [string, string, bigint];
      const blk = await this.provider!.getBlock(ev.blockNumber);
      rows.push({
        token: 'SWM',
        amount: this.normalizeAmount(value, 9),
        operation: 'Transfer Out',
        quantity: 1,
        source: from,
        destination: to,
        blockNumber: ev.blockNumber,
        txHash: ev.transactionHash,
        timestamp: blk ? new Date(blk.timestamp * 1000) : undefined,
      });
    }

    for (const ev of inLogs) {
      const log = ev as ethers.EventLog;
      const [from, to, value] = log.args as unknown as [string, string, bigint];
      const blk = await this.provider!.getBlock(ev.blockNumber);
      rows.push({
        token: 'SWM',
        amount: this.normalizeAmount(value, 9),
        operation: 'Transfer In',
        quantity: 1,
        source: from,
        destination: to,
        blockNumber: ev.blockNumber,
        txHash: ev.transactionHash,
        timestamp: blk ? new Date(blk.timestamp * 1000) : undefined,
      });
    }

    return rows;
  }

  async getPoolDeposits(userAddress: string): Promise<WalletTransactionRow[]> {
    const events = await poolHistoryService.fetchUserDepositEvents(userAddress);
    return events.map(e => ({
      token: 'SWM',
      amount: e.amount,
      operation: 'Deposit',
      quantity: 1,
      source: userAddress,
      destination: poolAddress,
      blockNumber: e.blockNumber,
      txHash: e.transactionHash,
      timestamp: e.timestamp,
    }));
  }

  async getWalletTransactions(
    userAddress: string
  ): Promise<WalletTransactionRow[]> {
    const [transfers, deposits] = await Promise.all([
      this.getSwmTransfers(userAddress).catch(() => []),
      this.getPoolDeposits(userAddress).catch(() => []),
    ]);

    return [...transfers, ...deposits].sort(
      (a, b) => b.blockNumber - a.blockNumber
    );
  }
}

export const walletTransactionsService = new WalletTransactionsService();
